"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import type * as React from "react";
import { createContext, useContext } from "react";
import { TextD2C } from "../../elements/text-d2c/text";
import { Column, Container, Row } from "../../layout/grid";
import type { D2CBackgroundColor, D2CTextColor } from "../../lib/d2c-colors";
import { cn } from "../../lib/utils";
import { Button } from "../button/button";
import type { AccordionInspectorProps } from "./accordion.preview";

const AccordionRoot = AccordionPrimitive.Root;

const AccordionItemContext = createContext(false);

export const useIsInsideAccordionItem = () => useContext(AccordionItemContext);

const accordionItemVariants = cva("border-b transition-all", {
  variants: {
    variant: {
      b2b: "border-border",
      d2c: "border-alabastergrey",
    },
  },
  defaultVariants: {
    variant: "b2b",
  },
});

function AccordionItem({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item> &
  VariantProps<typeof accordionItemVariants>) {
  return (
    <AccordionItemContext.Provider value={true}>
      <AccordionPrimitive.Item
        className={cn(accordionItemVariants({ variant }), className)}
        {...props}
      />
    </AccordionItemContext.Provider>
  );
}

const accordionTriggerVariants = cva(
  "group flex w-full items-center justify-between focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        b2b: "text-foreground transition-all",
        d2c: "group gap-4 py-8 text-left",
      },
      size: {
        default: "",
        lg: "",
      },
    },
    compoundVariants: [
      {
        variant: "b2b",
        size: "default",
        className: "py-4 font-semibold text-sm leading-5",
      },
      {
        variant: "b2b",
        size: "lg",
        className: "py-4 font-semibold text-2xl leading-8 tracking-tight",
      },
    ],
    defaultVariants: {
      variant: "b2b",
      size: "default",
    },
  }
);

function AccordionD2CIcon() {
  return (
    <span
      aria-hidden="true"
      className="relative flex size-6 shrink-0 items-center justify-center"
    >
      <span className="-translate-y-1/2 absolute inset-x-0 top-1/2 block h-px bg-current" />
      <span className="-translate-x-1/2 absolute inset-y-0 left-1/2 block w-px bg-current group-data-[state=open]:hidden" />
    </span>
  );
}

function AccordionTrigger({
  className,
  children,
  size,
  variant,
  triggerVariant = "default",
  resetId,
  onReset,
  resetDisabled = false,
  resetLabel = "Reset",
  ...props
}: Omit<React.ComponentProps<typeof AccordionPrimitive.Trigger>, "onReset"> &
  VariantProps<typeof accordionTriggerVariants> & {
    triggerVariant?: "default" | "reset";
    resetId?: string;
    onReset?: (id: string) => void;
    resetDisabled?: boolean;
    resetLabel?: string;
  }) {
  const isD2C = variant === "d2c";
  const iconSize = size === "lg" ? "size-5" : "size-4";
  const chevron = (
    <ChevronDown
      className={cn(
        iconSize,
        "shrink-0 text-muted-foreground transition-transform duration-200",
        "group-data-[state=open]:rotate-180"
      )}
    />
  );

  const leftContent = (() => {
    if (triggerVariant === "reset") {
      return (
        <div className="flex items-center gap-2">
          {chevron}
          <span>{children}</span>
        </div>
      );
    }
    if (isD2C) {
      return (
        <>
          {children}
          <AccordionD2CIcon />
        </>
      );
    }
    return (
      <>
        {children}
        {chevron}
      </>
    );
  })();

  const resetButton =
    triggerVariant === "reset" ? (
      <Button
        aria-label={resetLabel}
        className="ml-4 self-center"
        disabled={resetDisabled}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          if (resetDisabled) {
            return;
          }
          if (typeof onReset === "function" && resetId) {
            onReset(resetId);
          }
        }}
        size="sm"
        type="button"
        variant="ghost"
      >
        {resetLabel}
      </Button>
    ) : null;

  return (
    <AccordionPrimitive.Header className="flex items-center justify-between">
      <AccordionPrimitive.Trigger
        className={cn(accordionTriggerVariants({ variant, size }), className)}
        {...props}
      >
        {leftContent}
      </AccordionPrimitive.Trigger>

      {resetButton}
    </AccordionPrimitive.Header>
  );
}

const accordionContentVariants = cva(
  "overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
  {
    variants: {
      variant: {
        b2b: "text-foreground",
        d2c: "",
      },
      size: {
        default: "",
        lg: "",
      },
    },
    compoundVariants: [
      {
        variant: "b2b",
        size: "default",
        className: "font-normal text-sm leading-4",
      },
      {
        variant: "b2b",
        size: "lg",
        className: "font-normal text-base leading-5",
      },
    ],
    defaultVariants: {
      variant: "b2b",
      size: "default",
    },
  }
);

function AccordionContent({
  className,
  children,
  size,
  variant,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> &
  VariantProps<typeof accordionContentVariants>) {
  const isD2C = variant === "d2c";

  return (
    <AccordionPrimitive.Content
      className={cn(accordionContentVariants({ size, variant }), className)}
      {...props}
    >
      <div className={isD2C ? "pb-8" : "pt-0 pb-5"}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

const accordionD2CVariants = cva("", {
  variants: {
    columnWidth: {
      8: "col-span-10 col-start-2 md:col-span-8 md:col-start-3",
      10: "col-span-10 col-start-2",
      12: "col-span-12",
    },
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
    color: {
      black: "text-black",
      white: "text-white",
      alabastergrey: "text-alabastergrey",
    },
  },
  defaultVariants: {
    columnWidth: 10,
    backgroundColor: "white",
    color: "black",
  },
});

export interface AccordionItemData {
  id: string;
  label: string;
  children: React.ReactNode;
  inspectorProps?: Record<string, string>;
}

interface AccordionB2BProps {
  sys?: { id: string };
  mode: "B2B";
  items: AccordionItemData[];
  size?: "default" | "lg";
  className?: string;
  type?: "single" | "multiple";
  defaultValue?: string | string[];
  // Trigger visual variant for B2B: default | reset
  triggerVariant?: "default" | "reset";
  // Called when the reset control is clicked; receives the item id
  onReset?: (id: string) => void;
  // Accessible label for the reset control
  resetLabel?: string;
  inspectorProps?: AccordionInspectorProps;
}

interface AccordionD2CProps extends VariantProps<typeof accordionD2CVariants> {
  sys?: { id: string };
  mode: "D2C";
  items: AccordionItemData[];
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  columnWidth?: 8 | 10 | 12;
  backgroundColor?: D2CBackgroundColor;
  color?: D2CTextColor;
  className?: string;
  inspectorProps?: AccordionInspectorProps;
}
export type AccordionProps = AccordionB2BProps | AccordionD2CProps;

export function Accordion(props: Readonly<AccordionProps>) {
  if (props.mode === "D2C") {
    const containerClasses = accordionD2CVariants({
      columnWidth: null,
      backgroundColor: props.backgroundColor,
      color: props.color,
    });

    const columnClasses = accordionD2CVariants({
      columnWidth: props.columnWidth,
      backgroundColor: null,
      color: null,
    });

    return (
      <Container
        className={cn(containerClasses, "component-accordion", props.className)}
        fullWidth={true}
        paddingBottom={props.paddingBottom}
        paddingTop={props.paddingTop}
        testId="accordion"
        {...props.inspectorProps?.container}
      >
        <Row className="gap-0">
          <Column className={columnClasses}>
            <AccordionRoot
              className="border-alabastergrey border-t"
              type="multiple"
            >
              {props.items.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  variant="d2c"
                  {...item.inspectorProps}
                >
                  <AccordionTrigger variant="d2c">
                    <TextD2C style="no-mb">{item.label}</TextD2C>
                  </AccordionTrigger>
                  <AccordionContent variant="d2c">
                    {item.children}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </AccordionRoot>
          </Column>
        </Row>
      </Container>
    );
  }

  const accordionItems = props.items.map((item) => (
    <AccordionItem key={item.id} value={item.id} variant="b2b">
      <AccordionTrigger
        onReset={props.onReset}
        resetId={item.id}
        resetLabel={props.resetLabel}
        size={props.size}
        triggerVariant={props.triggerVariant}
        variant="b2b"
      >
        {item.label}
      </AccordionTrigger>
      <AccordionContent size={props.size} variant="b2b">
        {item.children}
      </AccordionContent>
    </AccordionItem>
  ));

  if (props.type === "single") {
    return (
      <AccordionRoot
        className={props.className}
        defaultValue={
          typeof props.defaultValue === "string"
            ? props.defaultValue
            : props.defaultValue?.[0]
        }
        type="single"
      >
        {accordionItems}
      </AccordionRoot>
    );
  }

  let multipleDefaultValue: string[] | undefined;

  if (Array.isArray(props.defaultValue)) {
    multipleDefaultValue = props.defaultValue;
  } else if (props.defaultValue) {
    multipleDefaultValue = [props.defaultValue];
  } else {
    multipleDefaultValue = undefined;
  }

  return (
    <AccordionRoot
      className={props.className}
      defaultValue={multipleDefaultValue}
      type="multiple"
    >
      {accordionItems}
    </AccordionRoot>
  );
}

export {
  AccordionRoot as AccordionPrimitive,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
};
